import React, { useEffect, useCallback } from "react";
import styled, { keyframes } from "styled-components";
import { designTokens, getTypography } from "../../styles/designTokens";

const fadeIn = keyframes`
  from { opacity: 0; transform: scale(0.97); }
  to { opacity: 1; transform: scale(1); }
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.45);
  display: flex;
  justify-content: center;
  align-items: center;
  backdrop-filter: blur(6px);
  z-index: 1000;
  animation: ${fadeIn} 0.25s ease forwards;
`;

const ModalContainer = styled.div<{ $variant: "default" | "danger" }>`
  background: rgba(255,255,255,0.94);
  border: 1px solid rgba(220,220,230,0.4);
  border-radius: 14px;
  padding: 32px 24px;
  width: fit-content;
  min-width: 360px;
  max-width: 90vw;
  display: flex;
  flex-direction: column;
  gap: 24px;
  box-shadow: 0 10px 28px rgba(10, 20, 40, 0.1);
  transition: 0.25s ease;

  ${({ $variant }) =>
    $variant === "danger" &&
    `
      border-color: rgba(255, 95, 95, 0.4);
      box-shadow: 0 10px 28px rgba(255, 95, 95, 0.15);
    `}
`;

const Title = styled.h3`
  ${getTypography("korean", "title1")};
  color: ${designTokens.colors.font.primary};
  text-align: center;
  margin: 0;
`;

const Message = styled.p`
  ${getTypography("korean", "body2")};
  color: ${designTokens.colors.font.secondary};
  text-align: center;
  white-space: pre-line;
  margin: 0;
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;
`;

const ButtonBase = styled.button`
  ${getTypography("korean", "body4")};
  height: 42px;
  min-width: 90px;
  border-radius: 8px;
  border: none;
  font-weight: 500;
  cursor: pointer;
  transition: 0.2s ease;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const CancelButton = styled(ButtonBase)`
  background: rgba(240, 240, 245, 0.8);
  color: ${designTokens.colors.font.primary};
  &:hover { background: rgba(230,230,240,0.9); }
  &:active { transform: translateY(1px); }
`;

const ConfirmButton = styled(ButtonBase)<{ $variant: "default" | "danger" }>`
  background: ${({ $variant }) =>
    $variant === "danger"
      ? "linear-gradient(135deg, #ff6666, #ff4b4b)"
      : "linear-gradient(135deg, #5c6eff, #7385ff)"};
  color: white;
  &:hover { filter: brightness(1.05); }
  &:active { transform: translateY(1px); }
`;

export interface ConfirmModalProps {
  isOpen: boolean;
  title?: string;
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
  confirmText?: string;
  cancelText?: string;
  variant?: "default" | "danger";
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen,
  title,
  message,
  onConfirm,
  onCancel,
  confirmText = "확인",
  cancelText = "취소",
  variant = "default",
}) => {
  const handleKey = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
      if (e.key === "Enter") onConfirm();
    },
    [onCancel, onConfirm]
  );

  useEffect(() => {
    if (!isOpen) return;
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [isOpen, handleKey]);

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={onCancel}>
      <ModalContainer
        $variant={variant}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        {title && <Title>{title}</Title>}
        <Message>{message}</Message>
        <ButtonGroup>
          <CancelButton onClick={onCancel}>{cancelText}</CancelButton>
          <ConfirmButton $variant={variant} onClick={onConfirm}>
            {confirmText}
          </ConfirmButton>
        </ButtonGroup>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default ConfirmModal;
